import { Alert, AlertDescription, AlertTitle, Button } from '@devizy/ui';
import {
  Briefcase,
  Gauge,
  ListChecks,
  PaperPlaneRight,
  RssSimple,
  UserCircle,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

import { VerificationStatusBanner } from '@/components/auth/verification-status-banner';
import { DashCard } from '@/components/dashboard/dash-card';
import { MetricCard } from '@/components/dashboard/metric-card';
import { QuickActionLink } from '@/components/dashboard/quick-action-link';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/dashboard/bidder/individual')({
  component: BidderIndividualDashboardPage,
});

function BidderIndividualDashboardPage(): React.ReactElement {
  const { data: profile } = trpc.bidder.me.useQuery();
  const { data: summary, isLoading } = trpc.bidder.dashboardSummary.useQuery();
  const incomplete = !profile?.legalIdentifier;
  const verificationStatus = profile?.verificationStatus ?? null;
  const metric = (value: number | undefined): string =>
    isLoading || value === undefined ? '—' : String(value);
  const matchScoreValue =
    isLoading || summary === undefined || summary.avgMatchScore === null
      ? '—'
      : String(summary.avgMatchScore);

  return (
    <>
      {incomplete ? (
        <Alert>
          <AlertTitle>Complétez votre profil</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              Votre profil Bidder Individuel n'est pas encore complet. Vous ne pouvez pas postuler
              sans profil vérifié.
            </p>
            <Button asChild>
              <Link to="/profile/bidder/individual">Compléter mon profil</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : verificationStatus ? (
        <VerificationStatusBanner
          status={verificationStatus}
          kind="INDIVIDUAL"
          reason={profile?.verificationRejectionReason ?? null}
        />
      ) : null}

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <MetricCard
          label="Candidatures actives"
          value={metric(summary?.activeBids)}
          loading={isLoading}
          icon={<PaperPlaneRight className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Projets en cours"
          value={metric(summary?.projectsInProgress)}
          loading={isLoading}
          icon={<Briefcase className="size-5" weight="regular" />}
        />
        <MetricCard
          label="Match Score moyen"
          value={matchScoreValue}
          loading={isLoading}
          icon={<Gauge className="size-5" weight="regular" />}
        />
      </section>

      <DashCard
        icon={<ListChecks className="size-5" weight="regular" />}
        title="Prochaines étapes"
        subtitle="Maximise tes chances sur le feed d'offres"
      >
        <div className="flex flex-col gap-2">
          <QuickActionLink
            href="/profile/bidder/individual"
            icon={<UserCircle className="size-5" weight="regular" />}
            label="Compléter mon profil"
            hint="Compétences, langues (CECRL) et portfolio"
          />
          <QuickActionLink
            href="/dashboard/bidder/feed"
            icon={<RssSimple className="size-5" weight="regular" />}
            label="Explorer le feed d'offres"
            hint="Trié par Match Score décroissant"
          />
        </div>
      </DashCard>
    </>
  );
}
